import React, { useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import { CheckCircle2, ArrowUpRight, Home, Sparkles, Calendar, Clock, ShieldCheck, HelpCircle } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Seo from "./Seo";
import { CORSI, findCourse, courseUrl } from "../mock";
import { CAL_LINK } from "./BookingModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const ALL_IN_LABEL = "Percorso completo All-In (5 corsi)";

export default function QuizBookingPage({ onOpenBooking }) {
  const [params] = useSearchParams();
  const courseSlug = params.get("course") || "";
  const name = params.get("name") || "";
  const email = params.get("email") || "";
  const isAllIn = params.get("allin") === "1";

  const course = useMemo(
    () => findCourse(courseSlug),
    [courseSlug]
  );
  const courseName = isAllIn ? ALL_IN_LABEL : (course?.title || "il tuo percorso personalizzato");
  const firstName = (name || "").split(" ")[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "quiz-booking" });
      cal("ui", {
        theme: "dark",
        hideEventTypeDetails: false,
        cssVarsPerTheme: {
          dark: {
            "cal-brand": "#d4ff3d",
            "cal-bg": "#0f0f0f",
            "cal-bg-emphasis": "#141414",
            "cal-bg-muted": "#111111",
            "cal-border": "rgba(255,255,255,0.1)",
            "cal-border-subtle": "rgba(255,255,255,0.06)",
            "cal-text": "#f5f5f5",
            "cal-text-emphasis": "#ffffff",
            "cal-text-muted": "rgba(255,255,255,0.6)",
          },
        },
        layout: "month_view",
      });
      // Fire Meta Schedule (Pixel) + GA4 schedule when the user confirms the booking.
      // Deduplication with server-side CAPI happens via the Cal.com booking uid.
      cal("on", {
        action: "bookingSuccessful",
        callback: async (e) => {
          try {
            const uid = e?.detail?.data?.booking?.uid || e?.detail?.data?.uid || "";
            const { trackSchedule } = await import("../lib/analytics");
            trackSchedule({ eventId: uid, contentName: "orientamento-quiz" });
          } catch (_) { /* analytics never breaks the flow */ }
        },
      });
    })();
  }, []);

  return (
    <div className="App grain min-h-screen" data-testid="quiz-booking-page">
      <Seo
        title="Prenota la tua call di orientamento | MyDigitalAction"
        noindex
      />
      <Navbar onOpenBooking={onOpenBooking} />

      {/* HERO */}
      <section className="pt-32 lg:pt-40 pb-8 lg:pb-12 relative overflow-hidden">
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-50" />
        <div className="container-max max-w-4xl relative">
          <div className="text-center">
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ background: "#d4ff3d", boxShadow: "0 0 40px rgba(212,255,61,0.35)" }}
            >
              <CheckCircle2 size={36} className="text-black" strokeWidth={2.5} />
            </div>
            <span className="section-label">Il tuo profilo è pronto</span>
            <h1 className="font-display text-3xl lg:text-5xl text-white mt-5 leading-[1.1]">
              {firstName ? `Perfetto ${firstName}, ` : "Perfetto, "}
              <span className="italic font-light text-white/70">il tuo percorso è</span>
              <br />
              <span className="text-white" data-testid="quiz-booking-course-name">{courseName}</span>
            </h1>
            <p className="text-white/70 text-base lg:text-lg mt-6 max-w-2xl mx-auto">
              Ora prenota la tua <span className="text-white font-medium">chiamata di orientamento gratuita con Frank</span> (30 minuti):
              commentiamo insieme il risultato e capiamo se questo è davvero il percorso giusto per te.
            </p>

            <div className="mt-8 flex items-center justify-center gap-6 flex-wrap text-white/60 text-xs">
              <div className="flex items-center gap-2">
                <Clock size={13} style={{ color: "#d4ff3d" }} />
                <span>30 min</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={13} style={{ color: "#d4ff3d" }} />
                <span>Videocall Google Meet</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={13} style={{ color: "#d4ff3d" }} />
                <span>100% gratuita, zero pressioni di vendita</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCY BAR — scarsità reale, sopra il calendario */}
      <div className="container-max max-w-3xl">
        <div
          className="flex items-center justify-center gap-3 py-7 lg:py-8 text-center"
          data-testid="quiz-booking-urgency-bar"
        >
          <Clock size={18} className="shrink-0" style={{ color: "#d4ff3d" }} />
          <p className="text-white/85 text-base lg:text-lg leading-snug">
            <span className="text-white font-medium">Apro solo 5 call di orientamento a settimana.</span>{" "}
            <span className="text-white/70">Prenota lo slot che preferisci prima che si esaurisca.</span>
          </p>
        </div>
      </div>

      {/* CAL.COM EMBED — immediately after hero */}
      <section className="pb-16">
        <div className="container-max max-w-5xl">
          <div
            className="bg-[#0f0f0f] border rounded-2xl overflow-hidden"
            style={{ borderColor: "rgba(212,255,61,0.35)", boxShadow: "0 0 60px rgba(212,255,61,0.06)" }}
            data-testid="quiz-booking-cal-embed"
          >
            <Cal
              namespace="quiz-booking"
              calLink={CAL_LINK}
              style={{ width: "100%", height: "auto", minHeight: "640px", overflow: "hidden" }}
              config={{
                layout: "month_view",
                theme: "dark",
                name: name || "",
                email: email || "",
              }}
            />
          </div>

          <div className="mt-6 flex items-center justify-between flex-wrap gap-3 text-xs text-white/50">
            <span>Fuso orario: <span className="text-white/70">Europe/Rome (GMT+1)</span></span>
            <span>Sync con Google Calendar · Conferma via email immediata</span>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL — social proof (subito dopo il calendario) */}
      <section className="pb-16">
        <div className="container-max max-w-5xl">
          <div className="text-center mb-8">
            <span className="section-label">Chi ha già parlato con Frank</span>
            <h3 className="font-display text-2xl lg:text-3xl text-white mt-3">
              Cosa dicono dopo aver lavorato con lui
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="quiz-booking-testimonials">
            {[
              { name: "Simona Adelaide Martini", role: "CEO di Psicoludìa", text: "Altissima competenza, professionalità e attenzione per i clienti. Segue da tempo la nostra società e ne siamo entusiaste." },
              { name: "Germano Bendinelli", role: "Titolare Studio Fisiomind", text: "Professionista serio, preparato e super disponibile. Consigliatissimo se cerchi un confronto vero." },
              { name: "Massimiliano Gallo", role: "Cliente Digital Formula", text: "Persona fantastica, disponibilità 100%. Un vero professionista da consigliare a chi cerca risultati." },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-card border border-white/10 rounded-2xl p-5 lg:p-6 flex flex-col"
                data-testid={`quiz-booking-testimonial-${i}`}
              >
                <div className="text-white/80 text-sm leading-relaxed flex-1">
                  <span className="text-white/30 text-2xl leading-none">"</span>
                  {t.text}
                  <span className="text-white/30 text-2xl leading-none">"</span>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-white text-sm font-medium">{t.name}</div>
                  <div className="text-white/50 text-xs mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHI È FRANK — cinematic embedded portrait */}
      <section className="relative overflow-hidden py-16 lg:py-28 scroll-mt-24" data-testid="quiz-booking-frank">
        {/* Desktop: portrait embedded on the LEFT side, fades into the black page toward the text (right) */}
        <div
          className="hidden md:block absolute inset-y-0 left-0 md:w-[62%] lg:w-[58%] max-w-[720px] pointer-events-none"
          aria-hidden="true"
        >
          <img
            src="/francesco-cutout.png"
            onError={(e) => { e.currentTarget.src = "/francesco-formatore-bw-neon.jpg"; }}
            alt=""
            className="w-full h-full object-contain opacity-95"
            style={{
              objectPosition: "25% top",
              WebkitMaskImage:
                "linear-gradient(to right, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 10%, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)",
              maskImage:
                "linear-gradient(to right, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 10%, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)",
              WebkitMaskComposite: "source-in",
              maskComposite: "intersect",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0a0a0a]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
        </div>

        <div className="container-max relative">
          {/* Mobile-only: portrait shown above the copy, faded at the bottom */}
          <div className="md:hidden relative mx-auto w-[240px] mb-6" aria-hidden="true">
            <img
              src="/francesco-cutout.png"
              onError={(e) => { e.currentTarget.src = "/francesco-formatore-bw-neon.jpg"; }}
              alt=""
              className="w-full h-auto block"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)",
                maskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)",
              }}
            />
          </div>

          <div className="max-w-lg md:ml-auto">
            <span className="section-label">Chi sono io</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-4 leading-[1.05]">
              Frank Gheri.{" "}
              <span className="italic font-light text-white/60">
                Imprenditore digitale, funnel manager, formatore.
              </span>
            </h2>
            <p className="text-white/75 text-base lg:text-lg mt-6 leading-relaxed">
              Fondatore di <span className="text-white font-medium">Digital Formula</span>: 10+ anni in agenzia, 200+ PMI supportate. Ideatore di{" "}
              <span className="text-white font-medium">My Digital Action</span>, l'academy che porta in aula il metodo che uso in azienda.
            </p>
            <p className="text-white/60 text-sm lg:text-base mt-4 leading-relaxed">
              In call zero pitch di vendita: 30 minuti per capire davvero da dove parti, dove vuoi arrivare e se questo è il percorso giusto per te. Se non lo è, te lo dico.
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
              <div>
                <div className="font-display text-3xl lg:text-4xl" style={{ color: "#6b8bff" }}>10+</div>
                <div className="text-white/50 text-[10px] mt-1 uppercase tracking-widest">Anni di agenzia</div>
              </div>
              <div>
                <div className="font-display text-3xl lg:text-4xl" style={{ color: "#6b8bff" }}>200+</div>
                <div className="text-white/50 text-[10px] mt-1 uppercase tracking-widest">PMI supportate</div>
              </div>
              <div>
                <div className="font-display text-3xl lg:text-4xl" style={{ color: "#6b8bff" }}>35h</div>
                <div className="text-white/50 text-[10px] mt-1 uppercase tracking-widest">Formazione dal vivo</div>
              </div>
              <div data-testid="quiz-booking-stat-rating">
                <div className="font-display text-3xl lg:text-4xl" style={{ color: "#6b8bff" }}>4.9/5</div>
                <div className="text-white/50 text-[10px] mt-1 uppercase tracking-widest">Valutazione media</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — domande frequenti sulla call */}
      <section className="pb-20 lg:pb-24" data-testid="quiz-booking-faq">
        <div className="container-max max-w-3xl">
          <div className="mb-8 lg:mb-10">
            <span className="section-label">Domande frequenti</span>
            <h3 className="font-display text-2xl lg:text-3xl text-white mt-3 leading-tight">
              Le risposte alle domande che ci fanno più spesso
            </h3>
          </div>
          <Accordion
            type="single"
            collapsible
            defaultValue="faq-1"
            className="w-full border-t border-white/10"
          >
            {[
              {
                id: "faq-1",
                q: "Devo comprare qualcosa in call?",
                a: "No, mai. Se durante la call sento che il momento è quello giusto per te, te lo dico e ti mando materiale per approfondire. Nessuna pressione, nessun pitch di vendita.",
              },
              {
                id: "faq-2",
                q: "Chi la fa la call?",
                a: "Io in persona, Frank Gheri. Nessun commerciale, nessun assistente. Sono io che parlo con te per 30 minuti.",
              },
              {
                id: "faq-3",
                q: "E se non è il corso giusto per me?",
                a: "Te lo dico apertamente e ti oriento verso altro, anche esterno a MyDigitalAction se serve. La call ha valore per te in ogni caso.",
              },
              {
                id: "faq-4",
                q: "Come si svolge?",
                a: "Google Meet, 30 minuti. Ricevi il link nell'email di conferma e ti si aggancia automaticamente al tuo Google Calendar.",
              },
              {
                id: "faq-5",
                q: "Posso spostarla o cancellarla?",
                a: "Sì, dal link nell'email di conferma fino a 2 ore prima dello slot prenotato.",
              },
            ].map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="border-b border-white/10 border-t-0"
                data-testid={`quiz-booking-faq-item-${item.id}`}
              >
                <AccordionTrigger className="py-5 lg:py-6 text-white text-base lg:text-lg font-medium hover:no-underline text-left [&[data-state=open]>svg]:text-[#d4ff3d]">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 text-sm lg:text-base leading-relaxed pb-5 lg:pb-6 pr-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECONDARY: skip to course */}
      {(course || isAllIn) && (
        <section className="pb-24">
          <div className="container-max max-w-3xl">
            <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: "#3b5bff" }} />
              <div className="relative">
                <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-white/70">
                  <Sparkles size={11} style={{ color: "#d4ff3d" }} /> Preferisci prima leggere il programma?
                </div>
                <h2 className="font-display text-white text-xl lg:text-2xl mt-4 leading-tight">
                  Scopri il dettaglio del percorso consigliato: {courseName}
                </h2>
                <p className="text-white/60 mt-3 text-sm">
                  Vedi programma, moduli, garanzia soddisfatti o rimborsati e testimonianze. Puoi sempre tornare qui a prenotare la call.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link
                    to={isAllIn ? "/#prezzo" : courseUrl(courseSlug)}
                    className="btn-secondary"
                    data-testid="quiz-booking-see-course-btn"
                  >
                    Vai al corso <ArrowUpRight size={16} />
                  </Link>
                  <Link
                    to="/"
                    className="text-white/50 hover:text-white text-sm inline-flex items-center gap-2 self-center"
                  >
                    <Home size={14} /> Torna alla home
                  </Link>
                </div>
              </div>
            </div>

            {email && (
              <p className="text-white/40 text-xs text-center mt-6">
                Abbiamo inviato il tuo risultato completo a{" "}
                <span className="text-white/70">{email}</span>. Controlla la casella (anche spam).
              </p>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
